import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { usePortalContainer } from '@/common/hooks/use-portal-container';

import { SlidersHorizontal, Plus } from 'lucide-react';
import { accessFormPopupActions } from '@common-reducers/AccessFormPopupReducer';
import { DBLabelThunks, labelSelectByQuery } from "@common-reducers/DBServiceThunks";
import { selectUserObject } from '@common-reducers/UserSelectors';
import { labelActions } from '@common-reducers/labels-reducer';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { useTranslation } from 'react-i18next';
import { namespace } from "@common-config/constants";
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
    DragStartEvent,
    DragOverlay,
    defaultDropAnimationSideEffects,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
    useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import LabelItem from './label-item';
import React from 'react';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover"

// Sortable wrapper for LabelItem
function SortableLabelItem({ label, onAction }: { label: any, onAction: () => void }) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: label._id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    return (
        <LabelItem
            label={label}
            setNodeRef={setNodeRef}
            style={style}
            attributes={attributes}
            listeners={listeners}
            isDragging={isDragging}
            onAction={onAction}
        />
    );
}

export default function LabelSettingsPopper() {
    const { t, i18n } = useTranslation(namespace);
    // The popover renders into a portal, so it inherits nothing from the top bar
    // and has to resolve its own direction — same reason the top bar does
    // (`applyDocumentDirection` never touches the WhatsApp host page). This was
    // pinned to 'ltr', which left the whole label manager laid out
    // left-to-right inside an otherwise mirrored UI.
    const dir: 'ltr' | 'rtl' = i18n.dir(i18n.language) === 'rtl' ? 'rtl' : 'ltr';
    const authUser = useAppSelector(selectUserObject);
    const [activeId, setActiveId] = useState<string | null>(null);
    const [open, setOpen] = useState(false);
    const dispatch = useAppDispatch()
    const containerRef = useRef<HTMLDivElement>(null);

    // Portal logic derived from LabelsManagerButton
    const localPortalContainer = usePortalContainer();
    const [globalPortalContainer, setGlobalPortalContainer] = useState<HTMLElement | ShadowRoot | null>(null);

    useEffect(() => {
        // Try to find the global portal container (LabelsModals)
        const globalPortal = document.getElementById('labels-modals')?.shadowRoot?.querySelector('.btcss-portal-container');
        if (globalPortal) {
            setGlobalPortalContainer(globalPortal as HTMLElement);
        }
    }, []);

    const targetContainer = globalPortalContainer || localPortalContainer;

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const labels = useAppSelector(state => labelSelectByQuery(state.DBLabelReducer,
        { $or: [{ deleted: { $exists: false } }, { deleted: false }] },
        [],
        { order: 1 }
    ))

    const activeLabel = activeId ? labels.find(l => l._id === activeId) : null;

    function handleDragStart(event: DragStartEvent) {
        setActiveId(event.active.id as string);
    }

    function handleDragEnd(event: DragEndEvent) {
        const { active, over } = event;
        setActiveId(null);

        if (active.id !== over?.id) {
            const oldIndex = labels.findIndex((l) => l._id === active.id);
            const newIndex = labels.findIndex((l) => l._id === over?.id);

            const newLabels = arrayMove(labels, oldIndex, newIndex);

            const changes = newLabels.map((label, index) => {
                return {
                    id: label._id,
                    changes: {
                        order: index + 1
                    }
                }
            })
            dispatch(DBLabelThunks.patchMany(changes))
        }
    }

    // Track last close time to prevent rapid close→reopen cycle caused by Shadow DOM event handling
    const lastCloseTimeRef = useRef<number>(0);

    const handleOpenChange = (newOpen: boolean) => {
        const now = Date.now();

        if (newOpen) {
            // If trying to open within 100ms of closing, ignore it - this is the Shadow DOM bug
            if (now - lastCloseTimeRef.current < 100) {
                return;
            }

            if (authUser?._id) {
                setOpen(true);
            } else {
                dispatch(accessFormPopupActions.setStatus('Login'));
                setOpen(false);
            }
        } else {
            lastCloseTimeRef.current = now;
            setOpen(false);
        }
    };

    const handleClose = () => setOpen(false);

    const dropAnimationLogger = {
        sideEffects: defaultDropAnimationSideEffects({
            styles: {
                active: {
                    opacity: '0.5',
                },
            },
        }),
    };

    return (
        <Popover open={open} onOpenChange={handleOpenChange}>
            <PopoverTrigger asChild>
                <button
                    title={t('labels.manageLabels')}
                    className="p-2 hover:bg-[var(--tasks-menu-hover-bg)] rounded-full transition-colors data-[state=open]:bg-[var(--tasks-menu-hover-bg)] outline-none"
                >
                    <SlidersHorizontal size={20} className="text-[var(--tasks-text-secondary)]" />
                </button>
            </PopoverTrigger>
            <PopoverContent
                align="start"
                className="w-auto p-0 border-none shadow-xl rounded-xl"
                container={targetContainer}
                zIndex={2147483647}
                // `dir` as well as the CSS property: Radix reads the attribute to
                // decide which way `align="start"` points, while the CSS drives
                // the content layout inside.
                dir={dir}
                style={{ direction: dir }}
            >
                <div ref={containerRef} className="max-h-[550px] w-[360px] overflow-hidden bg-[var(--tasks-menu-bg)] rounded-xl flex flex-col">
                    {/* Header */}
                    <div className="flex items-center justify-between h-[60px] px-4 border-b border-[var(--tasks-menu-border)] shrink-0">
                        <h3 className="text-base font-semibold text-[var(--tasks-text)]">
                            {t('labels.manageLabels')}
                        </h3>
                        <button
                            onClick={() => {
                                dispatch(labelActions.toggleNewLabel(true))
                                setOpen(false)
                            }}
                            className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-[var(--color-accent)] hover:bg-[var(--tasks-menu-hover-bg)] rounded-lg transition-colors"
                        >
                            <Plus size={16} />
                            {t('common.create')}
                        </button>
                    </div>

                    {/* Labels List */}
                    <div className="p-2 overflow-y-auto flex-1 min-h-0">
                        <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragStart={handleDragStart}
                            onDragEnd={handleDragEnd}
                        >
                            <SortableContext
                                items={labels.map(l => l._id ?? '')}
                                strategy={verticalListSortingStrategy}
                            >
                                {labels.map((label) => (
                                    <SortableLabelItem
                                        key={label._id}
                                        label={label}
                                        onAction={handleClose}
                                    />
                                ))}
                            </SortableContext>

                            {targetContainer && createPortal(
                                <DragOverlay dropAnimation={dropAnimationLogger}>
                                    {activeLabel ? (
                                        <LabelItem
                                            label={activeLabel}
                                            isDragging
                                            style={{ cursor: 'grabbing' }}
                                        />
                                    ) : null}
                                </DragOverlay>,
                                targetContainer as Element
                            )}
                        </DndContext>
                    </div>
                </div>
            </PopoverContent>
        </Popover>
    )
}
